/**
 * SupervisionLayoutContext
 *
 * Provides a toggle between 2–3 distinct visual styles/layouts for the supervision section:
 * - Clinical Minimal: Calm, spacious, low cognitive load
 * - Data-Dense Professional: Table-first, high information density
 * - Card-Based Modern: Card layout, visual hierarchy, touch-friendly
 *
 * Stored in localStorage so preference persists across sessions.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { SupervisionLayoutStyle } from '../types';

const STORAGE_KEY = 'unify-supervision-layout-style';

interface SupervisionLayoutContextValue {
  layoutStyle: SupervisionLayoutStyle;
  setLayoutStyle: (style: SupervisionLayoutStyle) => void;
}

const SupervisionLayoutContext = createContext<SupervisionLayoutContextValue | null>(null);

function readStoredStyle(): SupervisionLayoutStyle {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'clinical-minimal' || stored === 'data-dense' || stored === 'card-modern') {
      return stored;
    }
  } catch {
    // ignore
  }
  return 'clinical-minimal';
}

export function SupervisionLayoutProvider({
  children,
  defaultStyle,
}: {
  children: React.ReactNode;
  defaultStyle?: SupervisionLayoutStyle;
}): JSX.Element {
  const [layoutStyle, setLayoutState] = useState<SupervisionLayoutStyle>(
    () => defaultStyle ?? readStoredStyle()
  );

  const setLayoutStyle = useCallback((style: SupervisionLayoutStyle) => {
    setLayoutState(style);
    try {
      localStorage.setItem(STORAGE_KEY, style);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(
    () => ({ layoutStyle, setLayoutStyle }),
    [layoutStyle, setLayoutStyle]
  );

  return (
    <SupervisionLayoutContext.Provider value={value}>
      {children}
    </SupervisionLayoutContext.Provider>
  );
}

export function useSupervisionLayout(): SupervisionLayoutContextValue {
  const ctx = useContext(SupervisionLayoutContext);
  if (!ctx) {
    throw new Error('useSupervisionLayout must be used within SupervisionLayoutProvider');
  }
  return ctx;
}
